import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { ClockIcon, CheckIcon, PlayIcon, ThermometerIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function DailyRitualSection() {
  const videoRef = useRef<HTMLDivElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  // Slideshow state and images
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideshowRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const slideshowImages = [{
    url: 'https://cdn.shopify.com/s/files/1/0679/7135/0570/files/Gemini_Generated_Image_w514b2w514b2w514_5e73888a-d791-4bc5-bfdd-2668660e819e.png?v=1761123418',
    alt: '3 Powerful wellness therapies'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/j9fRtmdKkGR4xiHCyTjU1r/Gemini_Generated_Image_esm7r8esm7r8esm7_(1).webp',
    alt: 'Woman in pink sweater relaxing in bed with Oculis 3 eye therapy device'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/189tspUoSv7aL2a7GKQ71G/Gemini_Generated_Image_xgmywwxgmywwxgmy.webp',
    alt: 'Person wearing Oculis 3 eye massager relaxing on a couch'
  }, {
    url: 'https://cdn.shopify.com/s/files/1/0679/7135/0570/files/ChatGPT_Image_Sep_12_2025_02_50_22_AM.png?v=1760857231',
    alt: '3 Powerful wellness therapies'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/x15KZr4DBxcCwKHwvZc5mG/Gemini_Generated_Image_8cs0ff8cs0ff8cs0.webp',
    alt: 'Man lying down wearing Oculis 3 eye therapy device in a dark room'
  }, {
    url: 'https://cdn.shopify.com/s/files/1/0679/7135/0570/files/imgi_23_ChatGPT_Image_Sep_21_2C_2025_2C_09_57_18_PM__1.webp?v=1760858040',
    alt: '2 long time lovers enjoy love together'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ay79E3uDSuz8GaALxKBw92/Gemini_Generated_Image_rsluahrsluahrslu.webp',
    alt: 'Woman in white top wearing Oculis 3 eye therapy device in a bright room'
  }, {
    url: 'https://cdn.shopify.com/s/files/1/0679/7135/0570/files/Gemini_Generated_Image_pzxzompzxzompzxz.png?v=1760858847',
    alt: 'Heat like lipiflow'
  }, {
    url: 'https://cdn.shopify.com/s/files/1/0679/7135/0570/files/711Xha8JqbL._AC_SL1500_ab464591-5e30-4280-9f98-76cdeacd02ac.webp?v=1760858040',
    alt: 'Massager In Action'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/joYNVN82svKBLXpj3yFdrr/Gemini_Generated_Image_jj25imjj25imjj25_(1).webp',
    alt: 'Woman sitting up in bed putting on Oculis 3 eye therapy device'
  }];
  // Functions to control slideshow
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % slideshowImages.length);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? slideshowImages.length - 1 : prev - 1);
  };
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };
  // Auto-rotate slideshow
  useEffect(() => {
    // Start auto-rotation
    slideshowRef.current = setInterval(() => {
      nextSlide();
    }, 5000); // Change slide every 5 seconds
    // Cleanup on unmount
    return () => {
      if (slideshowRef.current) {
        clearInterval(slideshowRef.current);
      }
    };
  }, []);
  // Reset timer when manually changing slide
  useEffect(() => {
    if (slideshowRef.current) {
      clearInterval(slideshowRef.current);
      slideshowRef.current = setInterval(() => {
        nextSlide();
      }, 5000);
    }
  }, [currentSlide]);
  // Lazy load video thumbnail and prepare for autoplay on scroll
  useEffect(() => {
    if (!videoRef.current) return;
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !videoLoaded) {
        // First load just the thumbnail
        const img = document.createElement('img');
        img.src = 'https://img.youtube.com/vi/mKi1d8R6sJ8/maxresdefault.jpg';
        img.alt = 'Oculis 3 heat therapy demonstration video thumbnail';
        img.className = 'absolute top-0 left-0 w-full h-full object-cover';
        img.width = 640;
        img.height = 360;
        img.loading = 'lazy';
        img.decoding = 'async';
        if (videoRef.current) {
          videoRef.current.appendChild(img);
          // Add play button overlay
          const playButton = document.createElement('div');
          playButton.className = 'absolute inset-0 flex items-center justify-center bg-black bg-opacity-40';
          const playIcon = document.createElement('div');
          playIcon.className = 'bg-white bg-opacity-80 text-teal-600 rounded-full p-4 shadow-lg';
          playIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`;
          playButton.appendChild(playIcon);
          videoRef.current.appendChild(playButton);
          // Add click event to play muted video
          playButton.addEventListener('click', () => {
            if (!videoRef.current) return;
            // Remove thumbnail and play button
            videoRef.current.innerHTML = '';
            // Create and add iframe with muted autoplay
            const iframe = document.createElement('iframe');
            iframe.className = 'absolute top-0 left-0 w-full h-full';
            iframe.src = 'https://www.youtube.com/embed/mKi1d8R6sJ8?autoplay=1&mute=1&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&showinfo=0&loop=1&playlist=mKi1d8R6sJ8&iv_load_policy=3';
            iframe.title = 'Oculis 3 heat therapy demonstration';
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
            iframe.frameBorder = '0';
            iframe.loading = 'lazy';
            videoRef.current.appendChild(iframe);
          });
          setVideoLoaded(true);
          observer.disconnect();
        }
      }
    };
    const observer = new IntersectionObserver(handleIntersection, options);
    observer.observe(videoRef.current);
    return () => {
      observer.disconnect();
    };
  }, [videoLoaded]);
  return <section id="daily-ritual-section" className="py-12 border-b border-gray-200" data-id="element-239">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-240">
        The 15-Minute Daily Ritual That Breaks The Cycle
      </h2>
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-241">
        Introducing iRelief's OCULIS-3
      </h3>
      {/* Supporting Caption */}
      {/* Slideshow replacing the single image */}
      <div className="w-full mb-8 relative" data-id="element-242">
        <div className="rounded-lg overflow-hidden shadow-md relative" data-id="element-243">
          {/* Images */}
          {slideshowImages.map((image, index) => <div key={index} className={`w-full transition-opacity duration-500 ${currentSlide === index ? 'opacity-100 block' : 'opacity-0 hidden'}`} data-id="element-244">
              <img src={image.url} alt={image.alt} className="w-full h-auto rounded-lg shadow-md" width="600" height="400" loading={index === 0 ? 'eager' : 'lazy'} data-id="element-245" />
            </div>)}
          {/* Navigation arrows */}
          <button className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-2 shadow-md text-gray-800 hover:bg-opacity-100 transition-all z-10" onClick={prevSlide} aria-label="Previous slide" data-id="element-246">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-247" />
          </button>
          <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-70 rounded-full p-2 shadow-md text-gray-800 hover:bg-opacity-100 transition-all z-10" onClick={nextSlide} aria-label="Next slide" data-id="element-248">
            <ChevronRightIcon className="w-6 h-6" data-id="element-249" />
          </button>
        </div>
        {/* Slide indicators */}
        <div className="flex justify-center mt-3" data-id="element-250">
          {slideshowImages.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`w-2.5 h-2.5 mx-1 rounded-full transition-all ${currentSlide === index ? 'bg-teal-600 w-4' : 'bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-251" />)}
        </div>
      </div>
    <div className="bg-white rounded-md p-6 shadow-md leading-relaxed text-gray-800" data-id="element-252">
  <p className="mb-4" data-id="element-253"><strong className="text-teal-600" data-id="element-254">I found something different.</strong></p>

  <p className="mb-4" data-id="element-255">Not more drops. Not more prescriptions.</p>

  <p className="mb-4" data-id="element-256"><strong data-id="element-257">A daily ritual that actually works.</strong></p>

  <p className="mb-4" data-id="element-258">
    <strong className="text-teal-600" data-id="element-259">Oculis-3</strong> is inspired by <strong data-id="element-260">LipiFlow®.</strong> The $1,000+ in-office device many specialists use for temporary relief.
  </p>

  <p className="mb-4" data-id="element-261">
    The problem? It’s <strong data-id="element-262">expensive</strong>, often uncomfortable, and results typically last just 6–9 months.
  </p>

  <p className="mb-4" data-id="element-263">
    <strong className="text-teal-600" data-id="element-264">Oculis-3 can be used daily, at home.</strong>
  </p>

  <p className="mb-4" data-id="element-265">
    With gentle, consistent <strong data-id="element-266">42°C therapeutic heat</strong> to soften hardened oils, and relaxing <strong data-id="element-267">acupressure</strong> to encourage natural flow,
    this simple ritual brings <span className="text-teal-600 font-semibold" data-id="element-268">professional-grade comfort</span> into your own hands.
  </p>

  <p className="mb-4" data-id="element-269">
    Within days, I noticed the difference. Within weeks, I <strong data-id="element-270">rarely thought about my eyes at all...</strong>Which was exactly the point.
  </p>

  <p className="mb-0" data-id="element-271">
    <strong className="text-teal-600" data-id="element-272">The best part?</strong> It took me <strong data-id="element-273">15–20 minutes a day.</strong>
  </p>
    </div>

      {/* Three Comfort Technologies Feature Box */}
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-5 rounded-lg border border-teal-200 mb-6" data-id="element-274">
        <h4 className="text-lg font-bold text-teal-700 mb-3" data-id="element-275">
          Oculis-3 Triple-Action Comfort Technology
        </h4>
        <div className="space-y-4" data-id="element-276">
          <div className="flex items-start" data-id="element-277">
            <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3" data-id="element-278">
              <ThermometerIcon className="w-5 h-5 text-teal-600" data-id="element-279" />
            </div>
            <div data-id="element-280">
              <p className="font-semibold text-gray-800" data-id="element-281">
                Precision Temperature Control (42.5°C)
              </p>
              <p className="text-gray-700" data-id="element-282">
                The exact therapeutic temperature used to
                melt hardened oils and restore natural flow
              </p>
            </div>
          </div>
          <div className="flex items-start" data-id="element-283">
            <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3" data-id="element-284">
              <div className="w-5 h-5 text-teal-600 flex items-center justify-center" data-id="element-285">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-286">
                  <path d="M4 21c0-3 4.33-7 8-7s8 4 8 7" data-id="element-287"></path>
                  <path d="M4 10c0-3 4.33-7 8-7s8 4 8 7" data-id="element-288"></path>
                  <path d="M4 15c0-3 4.33-7 8-7s8 4 8 7" data-id="element-289"></path>
                </svg>
              </div>
            </div>
            <div data-id="element-290">
              <p className="font-semibold text-gray-800" data-id="element-291">
                Acupressure Massaging & Compression
              </p>
              <p className="text-gray-700" data-id="element-292">
                Mimics the $1,500 temporary in-office procedures that manually
                express blocked glands but you can do it daily at home
              </p>
            </div>
          </div>
          <div className="flex items-start" data-id="element-293">
            <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3" data-id="element-294">
              <div className="w-5 h-5 text-teal-600" data-id="element-295">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-296">
                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" data-id="element-297"></path>
                </svg>
              </div>
            </div>
            <div data-id="element-298">
              <p className="font-semibold text-gray-800" data-id="element-299">
                Relaxation Enhancement
              </p>
              <p className="text-gray-700" data-id="element-300">
                Bluetooth intergration to create a spa-like experience that
                makes this your favorite part of the day
              </p>
            </div>
          </div>
        </div>
        <div data-id="element-301">
        </div>
      </div>
    </section>;
}